// « Mains vives » (D-163, D-164) : la séance, en plein écran et en portrait (maquettes c3 à c7). Le calage (« Mains
// repérées », les 4 cibles à ta taille, « Refaire »), le décompte (la vidéo tourne déjà), le jeu (la cible annoncée, la main
// repérée, trois gros chiffres, le point d'enregistrement), la fin, puis la vidéo : « Garder la vidéo » ouvre la feuille de
// partage du téléphone (Photos), « Effacer ». La vidéo et l'image ne quittent jamais le téléphone ; Arceau reçoit seulement
// la durée, le niveau et les trois chiffres. Écran maintenu allumé (la parade iOS des drills, D-098). Une personne de plus
// dans l'image (plus de deux mains) : la séance se met en pause, la vidéo aussi (jamais un groupe filmé).
// D-165 : le niveau 4 « Dribble et réaction » (la main qui dribble repérée au rythme du poignet, la main libre touche les
// cibles de son côté ; « DRIBBLE ! » si le dribble s'arrête) ; à la fin, « Envoyer mon score au défi » pour un défi Mains
// vives de l'équipe (le score de CETTE séance, rien d'autre). Demandes d'Idries : la position jambes fléchies exigée (le
// modèle de pose ; « Fléchis les jambes », une cible touchée debout ne compte pas) ; les cibles espacées d'après la carrure.
import {
  COLORS, COUNTDOWN_S, DEFAULT_DRIBBLE, DribbleTracker, Game, HandSplit, HOME, SIDE_WORD, SWITCH_MS, TARGET_KEYS, ARROW, ARROW_WORD, WHERE, clock, distanceLabel,
  distanceOk, dist, handsRaised, keysFor, placeTargets, sec,
  StanceTracker, bodyOf, stanceSample,
  type Body, type DribblePlan, type HandObs, type Level, type Placement, type Pt, type Side, type StanceState, type Summary, type TargetKey,
} from '../domain/mains-vives';
import { agree, type TeamGender } from '../domain/labels';
import { mvChallengeSend, mvChallengesFor, mvSave, type MvChallenge } from '../lib/mains-vives-api';
import { beep, engage, playEndSound, soundPref, tick, unlockAudio } from './drill-engine';
import { armed, toast } from './dom';
import { mvIc } from './mv-art';
import { CameraError, essaiMode, essaiSource, openCamera, type CameraProblem, type Drive, type Source } from './mv-camera';
import { LocalRecorder, recordType, type Clip } from './mv-recorder';
import { drawStage, type Bottom, type StageHand, type StageState, type StageTarget } from './mv-stage';
import './mains-vives.css';

export interface RunOpts {
  /** La fiche du joueur (ses chiffres) ; le prénom ; son genre (accords). */
  personId: string;
  name: string;
  gender: TeamGender;
  /** Un parent lance la séance de son enfant mineur, sur son téléphone. */
  forChild: boolean;
  level: Level;
  durationS: number;
  sound: boolean;
  /** Le prénom du coach (« Arceau ne la reçoit pas, Marc non plus »), s'il y en a un. */
  coach: string | null;
  /** Niveau 4 : la main qui dribble (ou alterner). */
  dribble?: DribblePlan;
  /** La séance est lancée pour ce défi Mains vives (il passe en tête à la fin). */
  challengeId?: string | null;
  /** Après « Terminer » : la séance comptée ou non. */
  onClose: (saved: boolean) => void;
}

const K = 2;   // pixels du canevas par unité : 780 × 1688
let current: { stop: () => void } | null = null;

/** Le meilleur temps de réaction, gardé sur CE téléphone seulement (chaque caméra a son petit délai). */
const BEST_KEY = (person: string) => `arceau.mv.meilleur.${person}`;
export function localBest(person: string): number | null { try { const v = Number(localStorage.getItem(BEST_KEY(person))); return v > 0 ? v : null; } catch { return null; } }
function keepBest(person: string, ms: number): 'premier' | 'record' | null {
  const before = localBest(person);
  if (before != null && ms >= before) return null;
  try { localStorage.setItem(BEST_KEY(person), String(ms)); } catch { /* sans mémoire */ }
  return before == null ? 'premier' : 'record';
}

const esc = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

/** Ouvre la séance (appelé pendant le geste « Ouvrir la caméra » : le son et l'écran allumé s'y débloquent). */
export async function openRun(o: RunOpts): Promise<void> {
  current?.stop();
  unlockAudio();
  const root = document.createElement('div');
  root.className = 'mv-run';
  root.setAttribute('role', 'dialog'); root.setAttribute('aria-modal', 'true'); root.setAttribute('aria-label', 'Mains vives');
  root.innerHTML = `<div class="mv-frame"><canvas width="${390 * K}" height="${844 * K}" aria-hidden="true"></canvas>
    <button type="button" class="mv-hit mv-hit-x" aria-label="Arrêter la séance" data-mv-x>Arrêter</button>
    <button type="button" class="mv-hit mv-hit-redo" aria-label="Refaire le calage" data-mv-redo hidden>Refaire</button>
    <button type="button" class="mv-hit mv-hit-go" aria-label="Partir (au lieu de lever les mains)" data-mv-go hidden>Partir</button>
    <p class="mv-live" aria-live="polite"></p></div>`;
  document.body.append(root);
  const canvas = root.querySelector('canvas')!;
  const g = canvas.getContext('2d')!;
  const live = root.querySelector<HTMLElement>('.mv-live')!;
  const say = (s: string) => { if (live.textContent !== s) live.textContent = s; };
  const release = engage(root, 'Mains vives');
  void Promise.all(['400 76px Anton', '800 13px "JetBrains Mono"', '700 14px "Inter Variable"'].map(f => document.fonts?.load(f).catch(() => undefined)));

  let source: Source | null = null;
  let stopped = false;
  let raf = 0;
  const recorder = new LocalRecorder();
  const cleanup = () => {
    if (stopped) return;
    stopped = true;
    cancelAnimationFrame(raf);
    source?.stop(); source = null;
    recorder.abort();
    release();
    window.removeEventListener('hashchange', onLeave);
    window.removeEventListener('pagehide', onLeave);
    document.removeEventListener('visibilitychange', onVis);
    document.removeEventListener('keydown', onKey);
    root.remove();
    if (current?.stop === cleanup) current = null;
  };
  // Quitter la page (retour, autre écran) coupe tout : la caméra ne reste jamais allumée.
  const onLeave = () => cleanup();
  window.addEventListener('hashchange', onLeave);
  window.addEventListener('pagehide', onLeave);
  current = { stop: cleanup };

  // --- Sans caméra, pendant le chargement : un panneau, un message clair --------------------------------------------------
  const panel = (html: string) => {
    root.querySelector('.mv-panel')?.remove();
    if (!html) return;
    root.insertAdjacentHTML('beforeend', `<div class="mv-panel">${html}</div>`);
  };
  const loading = (what: 'camera' | 'modele') => panel(`<span class="mv-spin" aria-hidden="true"></span><p class="k">Mains vives</p>
    <h2>${what === 'camera' ? 'La caméra' : 'Le repérage des mains'}</h2>
    <p>${what === 'camera' ? 'Ton téléphone va demander l’accès à la caméra : touche « Autoriser ».' : 'Il se prépare sur ton téléphone. La première fois, il charge 28 Mo depuis Arceau ; rien n’est envoyé.'}</p>
    <div class="mv-btns"><button type="button" class="mv-btn alt" data-close aria-label="Fermer">Retour</button></div>`);
  const problem = (k: CameraProblem) => {
    const T: Record<CameraProblem, [string, string, string]> = {
      refus: ['Caméra refusée', 'Sans la caméra, le jeu ne voit pas tes mains. Rien n’a été filmé.',
        '<ol><li>iPhone : « aA » dans la barre d’adresse › Réglages du site web › Caméra › Autoriser (ou Réglages › Safari › Caméra).</li><li>Android : le cadenas à gauche de l’adresse › Autorisations › Caméra.</li></ol>'],
      absente: ['Pas de caméra', 'Cet appareil n’a pas de caméra avant utilisable. Le jeu se joue sur un téléphone posé.', ''],
      occupee: ['Caméra occupée', 'Une autre app se sert de la caméra (appel vidéo, appareil photo). Ferme-la, puis réessaie.', ''],
      ancien: ['Navigateur trop ancien', 'Mains vives a besoin d’un navigateur à jour : Safari (iOS 16.4 ou plus) ou Chrome. Mets-le à jour, puis reviens.', ''],
      modele: ['Le repérage n’a pas démarré', 'Vérifie ta connexion (la première fois, le téléphone charge 28 Mo), puis réessaie. Rien n’a été envoyé.', ''],
    };
    const [t, p, more] = T[k];
    source?.stop(); source = null;
    say(t);
    panel(`<p class="k">Mains vives</p><h2>${t}</h2><p>${p}</p>${more}
      <div class="mv-btns">${k === 'absente' || k === 'ancien' ? '' : '<button type="button" class="mv-btn main" data-mv-retry>Réessayer</button>'}
        <button type="button" class="mv-btn alt" data-close aria-label="Fermer">Retour</button></div>`);
    root.querySelector('[data-mv-retry]')?.addEventListener('click', () => { cleanup(); void openRun(o); });
  };
  root.addEventListener('click', e => { if ((e.target as HTMLElement).closest('[data-close]')) cleanup(); });

  // --- L'état de la séance -------------------------------------------------------------------------------------------------
  type Phase = 'search' | 'placed' | 'count' | 'play' | 'end';
  let phase: Phase = 'search';
  let placement: Placement | null = null;
  let stable = 0, lastSeenAt = 0, raisedSince = 0;
  const recent: HandObs[][] = [];
  let countAt = 0, playAt = 0, pausedMs = 0, pauseFrom = 0, crowdSince = 0, calmSince = 0;
  let paused: 'crowd' | 'hidden' | null = null;
  let armedAt = 0;
  let recAt = 0, recPaused = 0, recPauseFrom = 0, recording = false;
  let game: Game | null = null;
  let lastCall: Bottom | null = null;
  const floats: { x: number; y: number; text: string; sub: string; born: number }[] = [];
  const flashes = new Map<TargetKey, number>();
  const trails: [Pt[], Pt[]] = [[], []];
  const lvl = o.level;
  const redoBtn = root.querySelector<HTMLButtonElement>('[data-mv-redo]')!;
  const goBtn = root.querySelector<HTMLButtonElement>('[data-mv-go]')!;
  const xBtn = root.querySelector<HTMLButtonElement>('[data-mv-x]')!;
  const solo = agree(o.gender, 'seul', 'seule');
  // Niveau 4 (D-165) : la main qui dribble, repérée au rythme du poignet ; seule la main libre touche.
  const plan: DribblePlan | null = lvl === 4 ? o.dribble ?? DEFAULT_DRIBBLE : null;
  let tracker: DribbleTracker | null = null, split: HandSplit | null = null;
  let parts: { dribble: HandObs | null; free: HandObs | null } = { dribble: null, free: null };
  let dribbleOn = false, switchUntil = 0;
  const sideNow = (): Side => game?.side ?? (plan?.hand === 'L' ? 'L' : 'R');
  const otherSide = (s: Side) => (s === 'R' ? 'à gauche' : 'à droite');
  // La position (D-165) : le modèle de pose, quelques fois par seconde ; la carrure du calage place les cibles.
  const stance = new StanceTracker();
  let poseAt = 0, stanceNow: StanceState = 'unknown', straightSince = 0, lastNag = 0;
  let body: Body | null = null, bodyAt = 0, placedHands: HandObs[] | null = null, bodyUsed = false;
  const freshBody = (now: number) => (body && now - bodyAt < 1500 ? body : null);
  /** Les jambes tendues depuis 0,4 s (pendant le décompte et le jeu) : « Fléchis les jambes ». */
  const legsNag = (now: number) => (phase === 'count' || phase === 'play') && !paused && stanceNow === 'straight' && !!straightSince && now - straightSince >= 400;

  const recNow = (now: number) => (recording ? (now - recAt - recPaused - (recPauseFrom ? now - recPauseFrom : 0)) / 1000 : 0);
  const gameT = (now: number) => now - playAt - pausedMs - (pauseFrom ? now - pauseFrom : 0);

  function startCount(now: number) {
    phase = 'count'; countAt = now;
    redoBtn.hidden = true; goBtn.hidden = true;
    recording = recorder.start(canvas);
    recAt = now; recPaused = 0; recPauseFrom = 0;
    if (o.sound) beep(880);
    say('3');
  }
  function setPause(kind: 'crowd' | 'hidden' | null, now: number) {
    if (kind === paused || phase !== 'play') return;
    if (kind && !paused) { pauseFrom = now; recPauseFrom = now; recorder.pause(); }
    if (!kind && paused) { pausedMs += now - pauseFrom; pauseFrom = 0; recPaused += now - recPauseFrom; recPauseFrom = 0; recorder.resume(); }
    paused = kind;
    say(kind ? 'En pause' : 'Reprise');
  }
  const onVis = () => setPause(document.visibilityState === 'hidden' ? 'hidden' : null, performance.now());
  document.addEventListener('visibilitychange', onVis);

  async function finish(completed: boolean) {
    if (phase === 'end') return;
    phase = 'end';
    cancelAnimationFrame(raf);
    const summary = game?.summary() ?? null;
    if (o.sound && completed) playEndSound(soundPref());
    const clip = await recorder.stop().catch(() => null);
    source?.stop(); source = null;
    stopped = true;
    release();
    window.removeEventListener('hashchange', onLeave);
    window.removeEventListener('pagehide', onLeave);
    document.removeEventListener('visibilitychange', onVis);
    document.removeEventListener('keydown', onKey);
    root.remove();
    current = null;
    if (!summary) { o.onClose(false); return; }
    showEnd(o, summary, completed, clip, countAt && playAt ? (playAt - countAt) / 1000 : COUNTDOWN_S);
  }

  // La croix : avant le jeu, elle ferme ; pendant, deux touchers (« Arrêter ? », D-098).
  xBtn.addEventListener('click', () => {
    const now = performance.now();
    if (phase === 'search' || phase === 'placed' || root.querySelector('.mv-panel')) { cleanup(); return; }
    if (now - armedAt < 3000) { void finish(false); return; }
    armedAt = now;
    xBtn.setAttribute('aria-label', 'Arrêter ? Touche encore pour arrêter');
  });
  redoBtn.addEventListener('click', () => { placement = null; stable = 0; recent.length = 0; phase = 'search'; redoBtn.hidden = true; goBtn.hidden = true; say('Calage refait'); });
  goBtn.addEventListener('click', () => { if (phase === 'placed') startCount(performance.now()); });
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { if (phase === 'count' || phase === 'play') void finish(false); else cleanup(); } };
  document.addEventListener('keydown', onKey);

  // --- La source : la caméra (et MediaPipe), ou le mode d'essai des tests ----------------------------------------------------
  try {
    source = essaiMode() ? essaiSource() : await openCamera(root, k => { if (!stopped) problem(k); }, loading);
  } catch (e) {
    if (!stopped) problem(e instanceof CameraError ? e.kind : 'absente');
    return;
  }
  if (stopped) { source.stop(); return; }
  panel('');
  say('Mains vives : le calage. Debout face au téléphone, les deux mains devant toi.');

  // --- La boucle : lire les mains, avancer, dessiner -----------------------------------------------------------------------
  const frame = (now: number) => {
    if (stopped || phase === 'end' || !source) return;
    const aim = game?.lit && placement ? placement.targets[game.lit.key] : null;
    const drive: Drive = { phase: paused ? 'pause' : phase, aim, aimAt: game?.lit ? playAt + pausedMs + game.lit.at : 0,
      dribble: plan && (phase === 'count' || phase === 'play') ? sideNow() : null };
    source.tick(now, drive);
    const seen = source.seen();
    const fresh = seen.at !== lastSeenAt;
    lastSeenAt = seen.at;
    const hands = seen.hands;
    const ps = source.pose();
    if (ps.at !== poseAt) {
      poseAt = ps.at;
      stance.push(now, ps.frame ? stanceSample(ps.frame) : null);
      const b = ps.frame ? bodyOf(ps.frame) : null;
      if (b) { body = b; bodyAt = now; }
    }
    stanceNow = stance.state(now);
    if (stanceNow === 'straight') { if (!straightSince) straightSince = now; } else straightSince = 0;
    if (legsNag(now) && now - lastNag > 2500) {
      lastNag = now;
      if (o.sound) { beep(330, 0.14); setTimeout(() => { if (!stopped) beep(330, 0.14); }, 190); }
      say('Fléchis les jambes');
    }

    if (phase === 'search' && fresh) {
      if (hands.length >= 2) { recent.push(hands.slice(0, 2)); if (recent.length > 6) recent.shift(); stable++; } else { stable = 0; recent.length = 0; }
      if (stable >= 6) {
        const avg = (i: 0 | 1): HandObs => {
          const ps = recent.map(r => [...r].sort((a, b) => a.x - b.x)[i]!);
          return { x: ps.reduce((s, p) => s + p.x, 0) / ps.length, y: ps.reduce((s, p) => s + p.y, 0) / ps.length, size: ps.map(p => p.size).sort((a, b) => a - b)[Math.floor(ps.length / 2)]! };
        };
        placedHands = [avg(0), avg(1)];
        bodyUsed = !!freshBody(now);
        placement = placeTargets(placedHands, { level: lvl, body: freshBody(now) });
        if (placement && plan) { tracker = new DribbleTracker(placement.scale); split = new HandSplit((placement.targets.HL.x + placement.targets.HR.x) / 2); }
        if (placement) { phase = 'placed'; raisedSince = 0; redoBtn.hidden = false; goBtn.hidden = false; say('Mains repérées. Lève les deux mains pour partir.'); }
      }
    } else if (phase === 'placed' && placement) {
      // La carrure arrive après les mains (la pose se charge à côté) : les cibles se replacent une fois.
      if (!bodyUsed && placedHands && freshBody(now)) {
        bodyUsed = true;
        placement = placeTargets(placedHands, { level: lvl, body: freshBody(now) }) ?? placement;
        if (plan) split = new HandSplit((placement.targets.HL.x + placement.targets.HR.x) / 2);
      }
      if (handsRaised(hands, placement)) { if (!raisedSince) raisedSince = now; if (now - raisedSince > 250) startCount(now); } else raisedSince = 0;
    } else if (phase === 'count') {
      const left = COUNTDOWN_S - Math.floor((now - countAt) / 1000);
      if (left <= 0) {
        phase = 'play'; playAt = now;
        game = new Game({ level: lvl, durationMs: o.durationS * 1000, dribble: plan ?? undefined });
        if (o.sound) beep(1320, 0.32);
      } else if (String(left) !== live.textContent) { if (o.sound) beep(880); say(String(left)); }
    }
    // Niveau 4 : dès le décompte, la main qui dribble et son rythme ; la main libre seule touche les cibles.
    if (plan && tracker && split && (phase === 'count' || phase === 'play')) {
      parts = split.push(hands, sideNow());
      if (fresh) tracker.push(now, (parts.dribble?.wrist ?? parts.dribble)?.y ?? null);
      dribbleOn = tracker.state(now).on;
    }
    if (phase === 'play' && game && placement) {
      // Une personne de plus dans l'image : pause (et la vidéo aussi), reprise quand on est de nouveau seul.
      if (fresh) {
        if (seen.count > 2) { calmSince = 0; if (!crowdSince) crowdSince = now; if (now - crowdSince > 600) setPause('crowd', now); }
        else { crowdSince = 0; if (paused === 'crowd') { if (!calmSince) calmSince = now; if (now - calmSince > 600) setPause(null, now); } }
      }
      if (!paused) {
        for (const ev of game.step(gameT(now), plan ? (parts.free ? [parts.free] : []) : hands, placement.targets, { dribbling: dribbleOn, stance: stanceNow === 'straight' ? false : undefined })) {
          if (ev.type === 'lit') {
            const c = COLORS[ev.color]!;
            if (o.sound) beep(c.freq, 0.3);
            say(lvl === 3 ? `${ARROW_WORD[ev.key]} ${ev.key.endsWith('L') ? 'gauche' : 'droite'}` : c.word);
          } else if (ev.type === 'hit') {
            if (o.sound) tick(1560);
            const t = placement.targets[ev.key];
            floats.push({ x: t.x < 195 ? t.x + 40 : t.x - 92, y: t.y + 24, text: '+1', sub: `${sec(ev.ms)} s`, born: now });
            flashes.set(ev.key, now);
          } else if (ev.type === 'standing') {
            if (o.sound) beep(247, 0.18);
            const t = placement.targets[ev.key];
            floats.push({ x: t.x < 195 ? t.x + 40 : t.x - 150, y: t.y + 24, text: 'FLÉCHIS !', sub: 'debout, ça ne compte pas', born: now });
            say('Fléchis les jambes');
          } else if (ev.type === 'nodribble') {
            if (o.sound) beep(247, 0.18);
            const t = placement.targets[ev.key];
            floats.push({ x: t.x < 195 ? t.x + 40 : t.x - 150, y: t.y + 24, text: 'DRIBBLE !', sub: 'la touche ne compte pas', born: now });
            say('Dribble !');
          } else if (ev.type === 'switch') {
            tracker?.reset(); split?.reset(); dribbleOn = false;
            switchUntil = now + SWITCH_MS;
            if (o.sound) beep(660, 0.25);
            say(`Change de main : dribble main ${SIDE_WORD[ev.side]}`);
          } else if (ev.type === 'miss') {
            if (o.sound) beep(196, 0.2);
          } else if (ev.type === 'end') { void finish(true); return; }
        }
      }
    }
    draw(now, hands);
    const ph = paused ? 'pause' : phase;
    if (root.dataset.phase !== ph) root.dataset.phase = ph;   // l'état, pour les tests
    raf = requestAnimationFrame(frame);
  };

  function draw(now: number, hands: HandObs[]) {
    const lit = phase === 'play' ? game?.lit ?? null : null;
    // Les mains : la plus proche de la cible annoncée en plein, l'autre atténuée ; une traînée derrière elles.
    const sorted = [...hands].sort((a, b) => a.x - b.x).slice(0, 4);
    sorted.slice(0, 2).forEach((h, i) => { const tr = trails[i]!; tr.push({ x: h.x, y: h.y }); if (tr.length > 4) tr.shift(); });
    const litPt = lit && placement ? placement.targets[lit.key] : null;
    const near = litPt && sorted.length ? sorted.reduce((b, h) => (dist(h, litPt) < dist(b, litPt) ? h : b), sorted[0]!) : null;
    const drib = plan && (phase === 'count' || phase === 'play') ? parts.dribble : null;
    const sh: StageHand[] = sorted.map((h, i) => h === drib ? { x: h.x, y: h.y, label: 'DRIBBLE', tone: dribbleOn ? 'ok' as const : 'warn' as const } : ({
      x: h.x, y: h.y,
      label: phase === 'placed' && sorted.length === 2 ? (i === 0 ? 'GAUCHE' : 'DROITE') : undefined,
      dim: phase === 'play' && !!near && h !== near,
      trail: phase === 'play' && h === near && i < 2 ? trails[i]!.slice(0, -1).filter(p => dist(p, h) > 12) : undefined,
    }));
    const dribbleCheck = plan && (phase === 'count' || phase === 'play') && !paused ? [{ ok: dribbleOn, text: `Dribble main ${SIDE_WORD[sideNow()]}` }] : undefined;
    const alert = legsNag(now) ? 'Fléchis les jambes' : null;
    const poseState = source?.poseStatus() ?? 'failed';
    const colorAt = game?.colorAt ?? HOME;
    const targets: StageTarget[] = placement ? (lvl === 4 ? keysFor(4, sideNow()) : TARGET_KEYS).map(k => {
      const f = flashes.get(k);
      const flash = f ? Math.max(0, 1 - (now - f) / 350) : 0;
      const look = phase === 'placed' || phase === 'search' ? 'dash' : phase === 'count' ? 'idle'
        : lvl === 1 || lvl === 4 ? (lit?.key === k ? 'lit' : 'dim') : 'idle';
      return { key: k, color: colorAt[k], x: placement!.targets[k].x, y: placement!.targets[k].y, look, flash: flash || undefined };
    }) : [];
    for (let i = floats.length - 1; i >= 0; i--) if (now - floats[i]!.born > 1000) floats.splice(i, 1);
    const rec = recording && (phase === 'count' || phase === 'play') ? clock(recNow(now)) : null;
    const armedNow = now - armedAt < 3000 && (phase === 'count' || phase === 'play');
    const light = source?.luma() ?? null;
    let s: StageState;
    if (phase === 'search' || phase === 'placed') {
      const okLight = light == null || light >= 55;
      // « En position » (D-165) : jambes fléchies, d'après le modèle de pose ; s'il ne marche pas sur l'appareil, un avertissement.
      const posCheck = poseState === 'ready' ? { ok: stanceNow === 'bent', text: stanceNow === 'unknown' ? 'Position ?' : 'En position' }
        : poseState === 'failed' ? { ok: false, text: 'Position non vérifiée' } : null;
      const checks = placement ? [{ ok: placement.fits, text: 'Toi en entier' }, { ok: okLight, text: 'Lumière' }, { ok: distanceOk(placement.distance), text: distanceLabel(placement.distance) }, ...(posCheck ? [posCheck] : [])]
        : [{ ok: okLight, text: 'Lumière' }, ...(posCheck ? [posCheck] : [])];
      const allOk = !!placement && checks.every(c => c.ok || c === posCheck && poseState === 'failed');
      const fix = !placement ? (o.forChild ? `${o.name} en position face au téléphone : jambes fléchies, les deux mains devant la poitrine.` : 'En position face au téléphone : jambes fléchies, les deux mains devant la poitrine.')
        : !placement.fits || placement.distance < 1.5 ? 'Recule d’un pas, au milieu de l’image : les cibles doivent tenir dedans.'
        : placement.distance > 3.5 ? 'Approche-toi d’un pas.' : !okLight ? 'Plus de lumière devant toi (pas de fenêtre dans ton dos).'
        : poseState === 'ready' && stanceNow !== 'bent' ? 'Mets-toi en position : jambes fléchies, comme en défense.' : '';
      s = {
        label: 'Mains vives · le calage', rec: null, armed: false,
        status: placement ? { ok: allOk, title: allOk ? 'Mains repérées' : 'Presque', sub: allOk ? (poseState === 'failed' ? 'Les cibles sont placées à ta taille. La position n’est pas vérifiée sur ce téléphone : garde les jambes fléchies.' : 'Les cibles sont placées à ta taille, en position.') : fix }
          : { ok: false, title: 'Tes deux mains ?', sub: fix },
        camera: (gg, w, h) => source?.draw(gg, w, h), targets, hands: sh, checks,
        bottom: placement ? { kind: 'go', title: 'Lève les deux mains', sub: 'jambes fléchies : le décompte part.', redo: true }
          : { kind: 'go', title: 'Recule de 3 pas', sub: 'On doit te voir des pieds jusqu’aux mains levées.', redo: false },
      };
    } else if (phase === 'count') {
      const left = Math.max(1, COUNTDOWN_S - Math.floor((now - countAt) / 1000));
      s = {
        label: `Niveau ${lvl} · ${o.durationS} s`, rec, armed: armedNow, nums: { hits: '0', avg: '–', streak: '0', dim: true },
        camera: (gg, w, h) => source?.draw(gg, w, h), targets, hands: sh, count: left, checks: dribbleCheck, alert,
        bottom: { kind: 'count', title: plan ? 'Dribble' : 'En garde', step: COUNTDOWN_S + 1 - left,
          sub: plan ? `Le ballon en main ${SIDE_WORD[sideNow()]} : dribble dès maintenant, les cibles s’allument ${otherSide(sideNow())}.` : 'Genoux fléchis, mains devant. Les cibles s’allument à zéro.' },
      };
    } else {
      const gm = game!;
      const t = gameT(now);
      let bottom: Bottom;
      if (lit) {
        const c = COLORS[lit.color]!;
        bottom = lvl === 3 ? { kind: 'call', color: '#FFFFFF', ink: '#111827', arrow: ARROW[lit.key], word: ARROW_WORD[lit.key], left: clock(gm.left(t) / 1000) }
          : { kind: 'call', color: c.hex, ink: c.ink, shape: c.shape, word: c.word, left: clock(gm.left(t) / 1000) };
        lastCall = bottom;
      } else if (plan && now < switchUntil) bottom = { kind: 'go', title: 'Change de main', sub: `Dribble main ${SIDE_WORD[sideNow()]} ; les cibles passent ${otherSide(sideNow())}.`, redo: false };
      else bottom = lastCall ? { ...lastCall, left: clock(gm.left(t) / 1000) } as Bottom
        : { kind: 'call', color: '#3A3F47', ink: '#FFFFFF', word: plan ? 'Dribble' : 'Prêt', left: clock(gm.left(t) / 1000) };
      s = {
        label: `Mains vives · niveau ${lvl}`, rec, armed: armedNow,
        nums: { hits: String(gm.hits), avg: sec(gm.avgMs), streak: String(gm.streak), dim: false },
        camera: (gg, w, h) => source?.draw(gg, w, h), targets, hands: sh, checks: dribbleCheck, alert,
        floats: floats.map(f => ({ x: f.x, y: f.y, text: f.text, sub: f.sub, alpha: Math.max(0, 1 - (now - f.born) / 1000) })),
        pause: paused ? { title: 'En pause', sub: paused === 'crowd' ? `Quelqu’un d’autre est dans l’image : la séance reprend quand tu es ${solo}.` : 'La séance reprend quand tu reviens.' } : null,
        bottom, glow: lit && lvl !== 3 ? COLORS[lit.color]!.hex : null, prog: t / gm.durationMs,
      };
    }
    drawStage(g, s, K);
  }
  raf = requestAnimationFrame(frame);
}

// --- La fin (c6) et la vidéo (c7) ----------------------------------------------------------------------------------------------
function sheet(html: string, label: string): HTMLElement {
  document.querySelector('.mv-sheet')?.remove();
  const d = document.createElement('div');
  d.className = 'mv-sheet'; d.setAttribute('role', 'dialog'); d.setAttribute('aria-modal', 'true'); d.setAttribute('aria-label', label);
  d.innerHTML = html;
  document.body.append(d);
  d.scrollTop = 0;
  return d;
}
const mo = (b: number) => `${Math.max(1, Math.round(b / 1048576))} Mo`;

function showEnd(o: RunOpts, s: Summary, completed: boolean, clip: Clip | null, lead: number): void {
  let video = clip;
  let kept = false;
  let saved: 'oui' | 'non' | 'echec' = 'non';
  const record = completed && s.best ? keepBest(o.personId, s.best.ms) : null;
  const ton = o.forChild ? 'son' : 'ton';
  let saving: Promise<void> = Promise.resolve();
  // D-165 : la séance gardée, les défis Mains vives ouverts de l'équipe ; « Envoyer mon score au défi ».
  let sessionId: string | null = null;
  let chs: MvChallenge[] = [];
  const sent = new Set<string>();
  const save = () => (saving = (async () => {
    try { sessionId = await mvSave(o.personId, s); saved = 'oui'; } catch { saved = 'echec'; }
    if (saved === 'oui') chs = await mvChallengesFor(o.personId).catch(() => [] as MvChallenge[]);
  })());
  const close = async (el: HTMLElement, notify = true) => {
    if (video) URL.revokeObjectURL(video.url);
    el.remove();
    await saving;
    if (notify) o.onClose(saved === 'oui');
  };

  const render = () => {
    const best = s.best;
    const where = best ? `la cible ${COLORS[best.color]!.fem}, ${WHERE[best.key]}, à ${clock(lead + best.at / 1000)}` : '';
    const vline = video ? `<div class="mv-vline"><i>${mvIc('film')}</i><span><strong>${o.forChild ? 'La' : 'Ta'} vidéo est prête · ${clock(video.seconds)}</strong><small>Elle reste sur ton téléphone.</small></span></div>`
      : `<div class="mv-vline"><i>${mvIc('film')}</i><span><strong>${clip ? 'Vidéo effacée' : 'Pas de vidéo cette fois'}</strong><small>${clip ? 'Rien n’a quitté ton téléphone.' : 'Ce navigateur ne sait pas l’enregistrer ; les chiffres, si.'}</small></span></div>`;
    const status = !completed ? `<div class="mv-vline mv-warn"><i>${mvIc('chart')}</i><span><strong>Séance arrêtée</strong><small>Elle ne compte pas dans ${o.forChild ? 'sa' : 'ta'} progression.</small></span></div>`
      : saved === 'echec' ? `<div class="mv-vline mv-warn"><i>${mvIc('chart')}</i><span><strong>Chiffres pas encore gardés</strong><small>Pas de réseau ? <button type="button" class="mv-link" data-mv-resave>Réessayer</button></small></span></div>` : '';
    // Les défis Mains vives ouverts (celui de la séance en tête) : envoyer le score s'il est au niveau et à la durée du défi.
    const valOf = (c: MvChallenge) => (c.mv_measure === 'avg' ? (s.avgMs != null ? `${sec(s.avgMs)} s de moyenne` : null) : `${s.hits} touchée${s.hits > 1 ? 's' : ''}`);
    const fits = (c: MvChallenge) => c.mv_level === s.level && c.mv_duration_s === s.durationS && !!valOf(c);
    const list = completed && saved === 'oui' ? [...chs].sort((a, b) => Number(b.id === o.challengeId) - Number(a.id === o.challengeId)).slice(0, 3) : [];
    const sendable = list.some(c => fits(c) && !sent.has(c.id));
    const chal = list.map(c => sent.has(c.id)
      ? `<div class="mv-vline mv-chal"><i>${mvIc('check')}</i><span><strong>Envoyé au défi « ${esc(c.title)} »</strong><small>${valOf(c)} · <button type="button" class="mv-link" data-mv-board="${c.team_id}">Voir le classement</button></small></span></div>`
      : !fits(c) ? `<div class="mv-vline mv-chal mv-off"><i>${mvIc('chart')}</i><span><strong>Défi « ${esc(c.title)} »</strong><small>${!valOf(c) ? 'Aucune cible touchée : pas de temps moyen à envoyer.' : `Il se joue au niveau ${c.mv_level} en ${c.mv_duration_s} s : pas cette partie.`}</small></span></div>`
      : `<div class="mv-vline mv-chal"><i>${mvIc('chart')}</i><span><strong>Défi « ${esc(c.title)} »</strong><small>${o.forChild ? 'Son' : 'Ton'} score : ${valOf(c)}. Seulement le chiffre, ni vidéo ni image.</small></span></div>
        <button type="button" class="mv-btn main" data-mv-send="${c.id}">${o.forChild ? `Envoyer le score de ${esc(o.name)} au défi` : 'Envoyer mon score au défi'}</button>`).join('');
    const hand = s.level === 4 && o.dribble ? ` · ${o.dribble.hand === 'A' ? 'en alternant' : `main ${SIDE_WORD[o.dribble.hand]}`}` : '';
    const el = sheet(`<div class="mv-end" data-mv-end>
      <div class="hd"><div><p>Mains vives · niveau ${s.level} · ${s.durationS} s${hand}</p><h2>${completed ? 'Terminé !' : 'Arrêté'}</h2></div><button type="button" class="mv-x" aria-label="Fermer le bilan" data-mv-done-x>×</button></div>
      <div class="mv-stats">
        <div><b>${s.hits}</b><span>touchées<small>sur ${s.total} cible${s.total > 1 ? 's' : ''}</small></span></div>
        <div><b>${sec(s.avgMs)}</b><span>secondes de moyenne<small>${ton} temps de réaction</small></span></div>
        <div><b>${s.streak}</b><span>de série<small>la meilleure, sans raté</small></span></div>
      </div>
      ${best ? `<div class="mv-best"><span class="k">Meilleur temps${record ? ` <em>${record === 'premier' ? 'premier record' : 'record'}</em>` : ''}</span><b>${sec(best.ms)} s</b><span>${where}</span></div>` : ''}
      ${chal}${status}${vline}
      <div class="mv-btns">${video ? `<button type="button" class="mv-btn ${sendable ? 'alt' : 'main'}" data-mv-see>▶ Voir la vidéo</button>` : ''}
        <button type="button" class="mv-btn alt" data-mv-again>↻ Encore une</button><button type="button" class="mv-btn alt" data-mv-done>Terminer</button></div>
    </div>`, 'Mains vives : le bilan');
    el.querySelector('[data-mv-see]')?.addEventListener('click', () => showVideo());
    el.querySelector('[data-mv-resave]')?.addEventListener('click', async () => { await save(); render(); if (saved === 'oui') toast('Chiffres gardés'); });
    const lose = (b: HTMLElement, label: string) => !!video && !kept && !armed(b, label);
    for (const b of el.querySelectorAll<HTMLButtonElement>('[data-mv-send]')) b.addEventListener('click', async () => {
      const id = b.dataset.mvSend!;
      if (!sessionId) return;
      b.disabled = true;
      try { await mvChallengeSend(sessionId, id); sent.add(id); toast('Score envoyé au défi'); render(); }
      catch (e) { b.disabled = false; toast(/déjà envoyé/.test((e as Error).message ?? '') ? 'Déjà envoyé à ce défi' : 'Envoi impossible pour l’instant.', 'ko'); }
    });
    for (const b of el.querySelectorAll<HTMLElement>('[data-mv-board]')) b.addEventListener('click', () => {
      if (lose(b, 'La vidéo sera effacée : voir le classement ?')) return;
      const team = b.dataset.mvBoard!;
      void close(el, false).then(() => { location.hash = `#/defis/${team}/defis`; });
    });
    el.querySelector<HTMLElement>('[data-mv-again]')!.addEventListener('click', e => {
      if (lose(e.currentTarget as HTMLElement, 'La vidéo sera effacée : encore une ?')) return;
      void close(el, false).then(() => openRun(o));
    });
    for (const sel of ['[data-mv-done]', '[data-mv-done-x]']) el.querySelector<HTMLElement>(sel)!.addEventListener('click', e => {
      const b = e.currentTarget as HTMLElement;
      if (b.classList.contains('mv-x') && video && !kept) { (el.querySelector('[data-mv-done]') as HTMLElement).click(); return; }
      if (lose(b, 'La vidéo sera effacée : terminer ?')) return;
      void close(el);
    });
  };

  const showVideo = () => {
    if (!video) return;
    const when = new Date();
    const day = when.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
    const hour = `${when.getHours()} h ${String(when.getMinutes()).padStart(2, '0')}`;
    const el = sheet(`<div class="mv-vid" data-mv-vid>
      <div class="hd"><button type="button" class="mv-back" data-mv-back>‹ Bilan</button><span class="mv-when">${day} · ${hour}</span></div>
      <h2>${o.forChild ? `La vidéo de ${esc(o.name)}` : 'Ta vidéo'}</h2>
      <div class="mv-keep"><i>${mvIc('lock')}</i><span><b>Elle reste sur ton téléphone.</b> Arceau ne la reçoit pas, ${o.coach ? `${esc(o.coach)} non plus` : 'ton coach non plus'}.</span></div>
      <div class="mv-thumbrow"><div class="mv-thumb"><video src="${video.url}#t=0.4" controls playsinline preload="metadata" aria-label="La vidéo de la séance"></video></div>
        <div class="mv-meta"><div class="mv-vbtns"><button type="button" class="mv-btn main" data-mv-keep>Garder la vidéo</button><button type="button" class="mv-btn alt" data-mv-erase>Effacer</button></div>
          <div class="row"><b>${clock(video.seconds)} · ${mo(video.blob.size)}</b>le décompte et les ${s.durationS} s, en ${video.ext.toUpperCase()}</div>
          <div class="row"><b>Ce que tu as vu</b>l’image, les cibles, ${o.forChild ? 'ses' : 'tes'} mains, les chiffres</div></div></div>
    </div>`, `Mains vives : ${o.forChild ? 'la' : 'ta'} vidéo`);
    el.querySelector('[data-mv-back]')!.addEventListener('click', render);
    el.querySelector('[data-mv-keep]')!.addEventListener('click', async () => {
      const v = video!;
      const d = new Date();
      const name = `mains-vives-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}.${v.ext}`;
      const file = new File([v.blob], name, { type: v.type });
      const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
      if (nav.canShare?.({ files: [file] }) && nav.share) {
        try { await nav.share({ files: [file], title: 'Mains vives' }); kept = true; toast('Choisis « Enregistrer la vidéo » pour la garder dans Photos'); return; }
        catch (e) { if ((e as { name?: string }).name === 'AbortError') return; }
      }
      const a = document.createElement('a');
      a.href = v.url; a.download = name; document.body.append(a); a.click(); a.remove();
      kept = true;
      toast('Vidéo enregistrée sur cet appareil');
    });
    el.querySelector<HTMLElement>('[data-mv-erase]')!.addEventListener('click', e => {
      if (!armed(e.currentTarget as HTMLElement, 'Effacer ?')) return;
      URL.revokeObjectURL(video!.url); video = null;
      toast('Vidéo effacée');
      render();
    });
  };

  render();
  if (completed) void save().then(() => { if ((saved === 'echec' || chs.length) && document.querySelector('[data-mv-end]')) render(); });
}

/** Le format de la vidéo de ce navigateur, pour l'écran d'installation (« en MP4 »). */
export const videoFormat = (): string | null => { const t = recordType(); return t ? (t.includes('mp4') ? 'MP4' : 'WebM') : null; };
